import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

// Actions
import { getCampaignPlanOverview } from "../../../actions/campaign-plan/CampaignPlanActions";
import { getBasicCampaignDetails } from "../../../actions/campaign/CampaignBasicInfoActions";
import { getCampaignMedia } from "../../../actions/campaign-media/CampaignMediaActions";
import { getBuyersList } from "../../../actions/buyer/BuyerActions";
import { openCampaignConfirmFormModal } from "../../actions/campaign-review/CampaignConfirmModalActions";

// Utils and Constants
import { defaultPagination } from "../../../constants/UrlConstants";
import { checkIsDigitalCampaign } from "../../utils/campaignPlanningUtil";

// Components
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import Spinner from "../../../components/spinner/Spinner";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import { isBuyerUserSpecific } from "../../../components/hoc/MediaAgencySpecific";
import {
  AddCityInCampaign,
  CampaignDetails,
  CancelCampaign,
  SendForConfirmation,
} from "../components/HeaderNav";
import CityDetailsTable from "../components/CityDetailsTable";
import { addCityAction } from "../components/CampaignRedirect";
import CampaignViewCards from "../components/CampaignViewCards";
import CampaignConfirmationForm from "../components/CampaignConfirmationForm";

// Page-Component
function DraftOverviewHeader({ campaign, campaignPlanOverview }) {
  const dispatch = useDispatch();
  const history = useHistory();

  const updateStateLoading = useSelector(
    (state) => state.c_campaignBasicInfo.updateStateLoading
  );

  if (!campaign) {
    return null;
  }

  const { id: campaignId, mediaCount } = campaign;

  // check isDigital campaign
  const isDigitalCampaign = checkIsDigitalCampaign(campaignPlanOverview);

  function handleAddCity() {
    addCityAction(campaignId, history, isDigitalCampaign);
  }

  function confirmationAction() {
    dispatch(openCampaignConfirmFormModal());
  }

  const EnableCancelCampaign = isBuyerUserSpecific(CancelCampaign);
  const EnableAddCityInCampaign = isBuyerUserSpecific(AddCityInCampaign);
  const EnableSendForConfirmation = isBuyerUserSpecific(SendForConfirmation);

  const pageTitle = <CampaignDetails campaign={campaign} />;
  const pageActions = (
    <div className="d-flex align-items-center">
      {EnableCancelCampaign && <EnableCancelCampaign campaignId={campaignId} />}
      {updateStateLoading && <Spinner className="mr-3" />}
      {EnableAddCityInCampaign && (
        <EnableAddCityInCampaign onClickAction={handleAddCity} />
      )}
      {EnableSendForConfirmation && (
        <EnableSendForConfirmation
          confirmAction={confirmationAction}
          isDisabled={!mediaCount}
        />
      )}
    </div>
  );

  return <PageHeader title={pageTitle} actions={pageActions} shadow={true} />;
}

/**
 * Page
 */
export default function CampaignDraftOverviewPage() {
  const dispatch = useDispatch();
  const { campaignId } = useParams();

  // campaign
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);

  // campaignPlan
  const campaignPlanOverview = useSelector(
    (state) => state.campaignPlan.campaignPlanOverview
  );
  const { planByCity = {} } = campaignPlanOverview || {};

  // Loadings
  const campaignPlanOverviewLoading = useSelector(
    (state) => state.campaignPlan.campaignPlanOverviewLoading
  );
  const basicCampaignDetailsLoading = useSelector(
    (state) => state.c_campaignBasicInfo.campaignLoading
  );
  const campaignMediasLoading = useSelector(
    (state) => state.campaignMedia.campaignMediaLoading
  );

  // Campaign confirm popup
  const openCampaignConfirmModal = useSelector(
    (state) => state.campaignConfirmModal.openModal
  );

  const isPageLoading =
    campaignPlanOverviewLoading ||
    basicCampaignDetailsLoading ||
    campaignMediasLoading;

  const { pageNo, pageSize, pageSizeMax } = defaultPagination;
  useEffect(() => {
    dispatch(getCampaignPlanOverview(campaignId));
    dispatch(getBasicCampaignDetails(campaignId));

    // campaign medias
    dispatch(getCampaignMedia(campaignId, pageNo, pageSize));

    // buyers list
    dispatch(getBuyersList("", pageNo, pageSizeMax));
  }, [dispatch, campaignId]);

  // Sidebar Settings
  const redirectLinkObj = {
    campaignOverviewLink: `/campaign/${campaignId}/draft`,
    citySummaryLink: `/campaign/${campaignId}/city/:cityId/draft`,
  };

  // page loading
  if (isPageLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <>
      {/* SideBar */}
      <CampaignSidebar campaign={campaign} redirectLinkObj={redirectLinkObj} />

      {/* Content-Section */}
      <div className="content-wrapper">
        <DraftOverviewHeader
          campaign={campaign}
          campaignPlanOverview={campaignPlanOverview}
        />

        {/* Page-Content */}
        <div className="page-content">
          <CampaignViewCards
            campaign={campaign}
            campaignPlanOverview={campaignPlanOverview}
            mediaCount={campaign.mediaCount}
          />

          {/* City Details Table */}
          <CityDetailsTable planByCity={planByCity} campaign={campaign} />
        </div>

        {/* MODALS */}
        {openCampaignConfirmModal && (
          <CampaignConfirmationForm campaign={campaign} />
        )}
      </div>
    </>
  );
}
